import { useLazyQuery, useMutation } from "@apollo/client"
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import {
  productItemCreate,
  productItemDelete,
} from "../services/graphql/mutations"
import { productItemList } from "../services/graphql/queries"
import { deepCopy, logger, Status } from "../services/utilities/utility"
import { MainContext } from "./main"
import ProductCode from "../models/productCode"

interface MarketplaceCodesContextInterface {
  loading: boolean
  setLoading: Dispatch<SetStateAction<boolean>>
  updatingList: boolean
  setUpdatingList: Dispatch<SetStateAction<boolean>>
  editMode: boolean
  setEditMode: Dispatch<SetStateAction<boolean>>
  codesList: ProductCode[]
  setCodesList: Dispatch<SetStateAction<ProductCode[]>>
  getCodesList: (productId: string) => void
  doneChanges: boolean
  cancelChanges: () => void
  currentCode: ProductCode
  setCurrentCode: Dispatch<SetStateAction<ProductCode>>
  preChangesCurrentCode: ProductCode
  hasError: boolean
  createCodes: (
    productId: string,
    codes: {
      code: string
      inventoryCount: number
    }[]
  ) => Promise<ProductCode[] | void>
  codesListNextToken: string | null
  loadMoreCodes: (productId: string) => Promise<boolean>
  handleError: boolean
  setHandleError: Dispatch<SetStateAction<boolean>>
  deleteCode: (productId: string, itemId: string) => Promise<string | void>
}

const MarketplaceCodesContext = createContext<MarketplaceCodesContextInterface>(
  {
    loading: true,
    setLoading: () => {},
    updatingList: false,
    setUpdatingList: () => {},
    editMode: false,
    setEditMode: () => {},
    codesList: [],
    setCodesList: () => {},
    getCodesList: () => {},
    doneChanges: false,
    cancelChanges: () => {},
    currentCode: null,
    setCurrentCode: () => {},
    preChangesCurrentCode: null,
    hasError: false,
    createCodes: async () => {},
    codesListNextToken: null,
    loadMoreCodes: async () => true,
    handleError: false,
    setHandleError: () => {},
    deleteCode: async () => {},
  }
)

const MarketplaceCodesController = ({ children }: { children: ReactNode }) => {
  const { setError, setErrorMessage } = useContext(MainContext)

  // states
  const [loading, setLoading] = useState<boolean>(true) // loading
  const [updatingList, setUpdatingList] = useState<boolean>(false) // to show loader
  const [editMode, setEditMode] = useState<boolean>(true) // if all page should be disabled or not
  const [codesList, setCodesList] = useState<ProductCode[]>([]) // list of all codes
  const [doneChanges, setDoneChanges] = useState<boolean>(false) // if user has done changes
  const [currentCode, setCurrentCode] = useState<ProductCode>() // current code
  const [preChangesCurrentCode, setPreChangesCurrentCode] =
    useState<ProductCode>() // fetched current code
  const [codesListNextToken, setCodesListNextToken] = useState<string | null>(
    null
  ) // next token for codes list

  // errors for code edit
  const [handleError, setHandleError] = useState<boolean>(false)
  const hasError: boolean = handleError

  // reset all errors
  const resetErrors = () => {
    setHandleError(false)
  }

  // queries
  const [productItemListQuery] = useLazyQuery(productItemList)

  // mutations
  const [productItemCreateMutation] = useMutation(productItemCreate)
  const [productItemDeleteMutation] = useMutation(productItemDelete)

  // get codes list
  const getCodesList = useCallback(
    async (productId: string) => {
      setLoading(true)

      try {
        logger(Status.Api, "QUERY productItemList", productItemList)
        const { data } = await productItemListQuery({
          variables: {
            input: {
              productId,
              limit: 50,
            },
          },
        })
        logger(Status.Info, "codes list", data.productItemList.items)

        setCodesList(data.productItemList.items)
        setCodesListNextToken(data.productItemList.nextToken)

        setLoading(false)
        setUpdatingList(false)
      } catch (e: unknown) {
        if (e instanceof Error) {
          setError(true)
          setErrorMessage(e.message)
          logger(Status.Error, `productItemList`, e.message)
        }
        setLoading(false)
        setUpdatingList(false)
      }
    },
    [setError, setErrorMessage]
  )

  // load more codes
  const loadMoreCodes = async (productId: string) => {
    try {
      logger(Status.Api, "QUERY productItemList", productItemList)
      const { data } = await productItemListQuery({
        variables: {
          input: {
            productId,
            limit: 50,
            nextToken: codesListNextToken,
          },
        },
      })
      logger(Status.Info, "codes list", [
        ...codesList,
        ...data.productItemList.items,
      ])

      setCodesList([...codesList, ...data.productItemList.items])
      setCodesListNextToken(data.productItemList.nextToken)

      return true
    } catch (e: unknown) {
      if (e instanceof Error) {
        setError(true)
        setErrorMessage(e.message)
        logger(Status.Error, `productItemList`, e.message)
      }
      return false
    }
  }

  // create code
  const createCodes = async (
    productId: string,
    codes: {
      code: string
      inventoryCount: number
    }[]
  ) => {
    const BATCH_SIZE = 90

    const chunkedCodes = Array.from(
      { length: Math.ceil(codes.length / BATCH_SIZE) },
      (_, i) => codes.slice(i * BATCH_SIZE, i * BATCH_SIZE + BATCH_SIZE)
    )

    const allResults: ProductCode[] = []

    for (const batch of chunkedCodes) {
      try {
        logger(Status.Api, "MUTATION productItemCreate", productItemCreate)

        const { data } = await productItemCreateMutation({
          variables: {
            input: {
              productId,
              codes: batch,
            },
          },
        })

        logger(Status.Info, `code created`, data.productItemCreate)
        allResults.push(...data.productItemCreate)
      } catch (e: unknown) {
        if (e instanceof Error) {
          setError(true)
          setErrorMessage(e.message)
          logger(Status.Error, `productItemCreate`, e.message)
        }
      }
    }

    return allResults
  }

  // delete code
  const deleteCode = async (productId: string, itemId: string) => {
    try {
      logger(Status.Api, "MUTATION productItemDelete", productItemDelete)
      const { data } = await productItemDeleteMutation({
        variables: { input: { productId, itemId } },
      })
      logger(Status.Info, `code delete`, data.productItemDelete.itemId)

      return data.productItemDelete.itemId
    } catch (e: unknown) {
      if (e instanceof Error) {
        setError(true)
        setErrorMessage(e.message)
        logger(Status.Error, `productItemDelete`, e.message)
      }
    }
  }

  // cancel changes
  const cancelChanges = () => {
    setCurrentCode(deepCopy(preChangesCurrentCode))
    resetErrors()
  }

  // check if user has done changes
  useEffect(() => {
    if (currentCode) {
      const currentProductCopy = deepCopy(currentCode)
      const preChangesCurrentProductCopy = deepCopy(preChangesCurrentCode)

      delete currentProductCopy.team.document
      delete currentProductCopy.team.__typename
      delete preChangesCurrentProductCopy.team.document
      delete preChangesCurrentProductCopy.team.__typename

      if (
        JSON.stringify(currentProductCopy) !==
        JSON.stringify(preChangesCurrentProductCopy)
      ) {
        setDoneChanges(true)
      } else {
        setDoneChanges(false)
      }
    }
  }, [currentCode])

  return (
    <MarketplaceCodesContext.Provider
      value={{
        loading,
        setLoading,
        updatingList,
        setUpdatingList,
        editMode,
        setEditMode,
        codesList,
        setCodesList,
        getCodesList,
        doneChanges,
        cancelChanges,
        currentCode,
        setCurrentCode,
        preChangesCurrentCode,
        hasError,
        createCodes,
        codesListNextToken,
        loadMoreCodes,
        handleError,
        setHandleError,
        deleteCode,
      }}
    >
      {children}
    </MarketplaceCodesContext.Provider>
  )
}

export { MarketplaceCodesController, MarketplaceCodesContext }
